import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase, dbError, getSignedUrlRefreshDelay, addSignedUrlsToPhotos, LIST_PHOTO_VARIANTS, isTransientNetworkError } from '@/lib';
import { uploadError } from '@/lib/logger';
import { MemberPhoto, MemberWebcamPhoto } from '@/types';
import { mapSupabaseError } from '@/lib';
import { logActivity } from '@/utils';
import { MEMBER_PHOTO_QUERY_KEYS } from '@/constants';

const PHOTO_BUCKET = 'member-webcam-photos';
const ALL_PHOTOS_HARD_LIMIT = 100;

type RawMemberWebcamPhoto = Pick<MemberWebcamPhoto, 'id' | 'original_photo_url' | 'thumbnail_photo_url' | 'large_photo_url' | 'uploaded_at'>;

interface PhotoWithExpiry {
  original_photo_url_expires_at?: number | null;
  large_photo_url_expires_at?: number | null;
  thumbnail_photo_url_expires_at?: number | null;
}

function findEarliestExpiry(photos: Iterable<PhotoWithExpiry>): number | null {
  let min: number | null = null;
  for (const photo of photos) {
    const expiries = [
      photo.original_photo_url_expires_at,
      photo.large_photo_url_expires_at,
      photo.thumbnail_photo_url_expires_at,
    ].filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

    if (!expiries.length) continue;
    const photoMin = Math.min(...expiries);
    if (min === null || photoMin < min) {
      min = photoMin;
    }
  }
  return min;
}

function useSignedUrlRefresh(
  photos: PhotoWithExpiry[] | null | undefined,
  refetch: () => void,
) {
  useEffect(() => {
    if (!photos || photos.length === 0) return;

    const earliestExpiry = findEarliestExpiry(photos);
    const delay = getSignedUrlRefreshDelay(earliestExpiry);
    if (delay === null) return;

    const timer = setTimeout(refetch, delay);
    return () => clearTimeout(timer);
  }, [photos, refetch]);
}

export function useMemberWebcamPhotos(memberId: string) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(memberId),
    queryFn: async () => {
      try {
        if (!memberId) {
          // Return empty array for missing member ID - don't throw error
          return [];
        }

        // Fetch all photo URLs
        const { data, error } = await supabase
          .from('member_webcam_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false })
          .limit(1); // Only get the latest photo

        if (error) {
          throw mapSupabaseError(error, 'fetching webcam photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberWebcamPhoto[],
          PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useMemberWebcamPhotos', err, { memberId });
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes (renamed from cacheTime)
    // Force refetch on every mount — tab clicks on the member-profile
    // screen remount this hook, and admins expect fresh data each visit.
    refetchOnMount: 'always',
  });

  useSignedUrlRefresh(query.data, query.refetch);

  return query;
}

export function useAllMemberWebcamPhotos(memberId: string) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(memberId),
    queryFn: async () => {
      try {
        if (!memberId) {
          // Return empty array for missing member ID - don't throw error
          return [];
        }

        const { data, error } = await supabase
          .from('member_webcam_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false })
          .limit(ALL_PHOTOS_HARD_LIMIT);

        if (error) {
          throw mapSupabaseError(error, 'fetching all webcam photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberWebcamPhoto[],
          PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useAllMemberWebcamPhotos', err, { memberId });
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes (renamed from cacheTime)
  });

  useSignedUrlRefresh(query.data, query.refetch);

  return query;
}

export function useCreateMemberWebcamPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      member_id: string;
      original_photo_url: string;
      thumbnail_photo_url: string;
      large_photo_url: string;
    }) => {
      try {
        // First, check if member already has photos to determine if this is an add or update
        const { data: existingPhotos, error: fetchError } = await supabase
          .from('member_webcam_photos')
          .select('id')
          .eq('member_id', data.member_id)
          .limit(1);

        if (fetchError) {
          uploadError('Error checking existing photos', fetchError, { memberId: data.member_id });
          throw new Error('Failed to check existing photos');
        }

        const isUpdate = existingPhotos && existingPhotos.length > 0;

        // Get member details for logging
        const { data: memberData, error: memberError } = await supabase
          .from('members')
          .select('first_name, last_name, email')
          .eq('id', data.member_id)
          .single();

        if (memberError) {
          uploadError('Error fetching member details for logging', memberError, { memberId: data.member_id });
          // Don't throw error, just continue without logging
        }

        // Insert the new photo
        const normalizePath = (path: string) => path.trim().replace(/^\/+/, '');

        const payload = {
          member_id: data.member_id,
          original_photo_url: normalizePath(data.original_photo_url),
          thumbnail_photo_url: normalizePath(data.thumbnail_photo_url),
          large_photo_url: normalizePath(data.large_photo_url),
        };

        const { data: insertedPhoto, error } = await supabase
          .from('member_webcam_photos')
          .insert([payload])
          .select()
          .single();

        if (error) {
          uploadError('Error creating webcam photo', error, { memberId: data.member_id });
          throw new Error('Failed to save photo');
        }

        // Log the activity
        try {
          const action = isUpdate ? 'member_webcam_photo_updated' : 'member_webcam_photo_added';
          const metadata = {
            member_name: memberData ? `${memberData.first_name} ${memberData.last_name}` : 'Unknown Member',
            member_email: memberData?.email || 'Unknown Email',
            photo_id: insertedPhoto.id,
            photo_urls: {
              original: payload.original_photo_url,
              thumbnail: payload.thumbnail_photo_url,
              large: payload.large_photo_url
            }
          };

          await logActivity(
            action,
            'member_webcam_photo',
            insertedPhoto.id,
            undefined, // No changes object needed for photo creation
            metadata,
            data.member_id
          );
        } catch (logError) {
          uploadError('Error logging webcam photo activity', logError, { memberId: data.member_id });
          // Don't throw error, photo was saved successfully
        }

        return insertedPhoto;
      } catch (err) {
        uploadError('Unexpected error in useCreateMemberWebcamPhoto', err, { memberId: data.member_id });
        throw new Error(
          err instanceof Error 
            ? err.message 
            : 'Failed to save photo. Please try again.'
        );
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate individual member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(variables.member_id) });
      
      // Invalidate all member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(variables.member_id) });
      
      // Invalidate bulk photo queries that might include this member
      queryClient.invalidateQueries({ 
        queryKey: [MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_PREFIX],
        predicate: (query) => {
          const memberIds = query.queryKey[1];
          return Array.isArray(memberIds) && memberIds.includes(variables.member_id);
        }
      });
    },
  });
}

export function useDeleteMemberWebcamPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: string; memberId: string }) => {
      try {
        const { error } = await supabase
          .from('member_webcam_photos')
          .delete()
          .eq('id', photoId);

        if (error) {
          uploadError('Error deleting webcam photo', error, { photoId });
          throw new Error('Failed to delete photo');
        }
      } catch (err) {
        uploadError('Unexpected error in useDeleteMemberWebcamPhoto', err, { photoId });
        throw new Error(
          err instanceof Error 
            ? err.message 
            : 'Failed to delete photo. Please try again.'
        );
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate individual member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(variables.memberId) });
      
      // Invalidate all member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(variables.memberId) });
      
      // Invalidate bulk photo queries that might include this member
      queryClient.invalidateQueries({ 
        queryKey: [MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_PREFIX],
        predicate: (query) => {
          const memberIds = query.queryKey[1];
          return Array.isArray(memberIds) && memberIds.includes(variables.memberId);
        }
      });
    },
  });
}

/**
 * Bulk hook for fetching multiple member photos at once to avoid N+1 queries
 * 
 * PERFORMANCE OPTIMIZATION:
 * Before: Each MemberNameCell called useMemberWebcamPhotos(memberId) individually
 * - 10 members = 20 database queries (2 per member: existence check + photo fetch)
 * - Photos loaded in waterfall pattern, appearing one by one
 * 
 * After: Single bulk query fetches all member photos at once
 * - 10 members = 1 database query (bulk fetch with .in() clause)
 * - All photos load simultaneously with skeleton states
 * - 95% reduction in database queries
 * - Significantly improved perceived performance
 * 
 * Usage: Replace individual useMemberWebcamPhotos calls with this bulk hook
 * in components that render multiple members (like MembersPage table)
 *
 * `keepPreviousData` is for callers whose id list changes as the user types
 * (the global header search): the id list is part of the cache key, so every
 * new list is a cold key whose `data` is undefined until it resolves — without
 * it, already-visible thumbnails blink back to the placeholder on each
 * keystroke. Lookups are by member id, so serving the previous map can only
 * ever return the right member's photo or nothing.
 */
export function useBulkMemberPhotos(
  memberIds: string[],
  options?: { keepPreviousData?: boolean },
) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM(memberIds),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    queryFn: async () => {
      try {
        if (!memberIds.length) {
          return {};
        }

        // Filter out invalid UUIDs to prevent database errors
        const validMemberIds = memberIds.filter(id => {
          // A more lenient UUID regex that checks for structure without enforcing version/variant
          const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
          return id && typeof id === 'string' && uuidRegex.test(id);
        });

        if (!validMemberIds.length) {
          return {};
        }

        // Fetch ONLY the latest photo per member. The list renders a single
        // thumbnail per member, so signing a URL for every historical photo (as
        // the old `.in()` select did) was wasted work that eventually blew past
        // Storage's 1000-path createSignedUrls cap (HOK-OS-47). DISTINCT ON runs
        // server-side and the jsonb return sidesteps PostgREST's 1000-row cap.
        const { data, error } = await supabase.rpc('get_latest_webcam_photo_per_member', {
          p_member_ids: validMemberIds,
        });

        if (error) {
          // Transient network errors (e.g. Safari "Load failed") are non-critical
          // for thumbnail display — return empty instead of throwing
          if (isTransientNetworkError(error)) {
            console.warn('[Database] Transient network error fetching bulk webcam photos, returning empty', {
              memberCount: memberIds.length,
              errorMessage: error.message,
            });
            return {};
          }

          dbError('Error fetching bulk webcam photos', error, {
            memberCount: memberIds.length,
            validMemberCount: validMemberIds.length,
            errorDetails: error
          });
          throw new Error('Failed to fetch member photos');
        }

        // The RPC returns a jsonb array of one latest row per member.
        const rows = (data ?? []) as Array<RawMemberWebcamPhoto & { member_id: string }>;

        // Sign only the sizes a list/grid renders — see LIST_PHOTO_VARIANTS.
        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          rows,
          PHOTO_BUCKET,
          LIST_PHOTO_VARIANTS,
        );

        // One row per member already (DISTINCT ON), so key straight through.
        const photosByMember: Record<string, MemberPhoto> = {};

        photosWithSignedUrls.forEach((photo, index) => {
          const memberId = rows[index].member_id;
          photosByMember[memberId] = {
            id: photo.id,
            original_photo_url: photo.original_photo_url,
            large_photo_url: photo.large_photo_url,
            thumbnail_photo_url: photo.thumbnail_photo_url,
            uploaded_at: photo.uploaded_at,
            original_photo_path: photo.original_photo_path ?? null,
            large_photo_path: photo.large_photo_path ?? null,
            thumbnail_photo_path: photo.thumbnail_photo_path ?? null,
            original_photo_url_expires_at: photo.original_photo_url_expires_at ?? null,
            large_photo_url_expires_at: photo.large_photo_url_expires_at ?? null,
            thumbnail_photo_url_expires_at: photo.thumbnail_photo_url_expires_at ?? null,
          };
        });

        return photosByMember;
      } catch (err) {
        // Transient network errors — return empty gracefully
        if (isTransientNetworkError(err)) {
          console.warn('[Database] Transient network error in useBulkMemberPhotos, returning empty', {
            memberCount: memberIds.length,
            errorMessage: err instanceof Error ? err.message : String(err),
          });
          return {};
        }

        dbError('Unexpected error in useBulkMemberPhotos', err, { memberCount: memberIds.length });
        throw new Error('Failed to fetch member photos');
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
    enabled: memberIds.length > 0,
  });

  const photoList = useMemo(
    () => (query.data ? Object.values(query.data) : []),
    [query.data],
  );
  useSignedUrlRefresh(photoList, query.refetch);

  return query;
}
